import { FC, useEffect } from 'react';
import { useOrganizationByShortnameQuery } from '~/queries/organization/useOrganizationsQuery';
import { useWorkspaceByShortNameQuery } from '~/queries/workspace/useWorkspacesQuery';
import { useParams } from '~/hooks/useParams';
import { UIMatch, useMatches } from 'react-router-dom';

/**
 * Returns nearest title from route handle definition
 */
const getNearestTitle = (matches: UIMatch<unknown>[]): string | undefined => {
  for (let step = matches.length - 1; step >= 0; step -= 1) {
    const { handle } = matches[step];
    if (
      handle &&
      typeof handle === 'object' &&
      'title' in handle &&
      typeof handle.title === 'string'
    ) {
      return handle.title;
    }
  }
  return undefined;
};

export const Title: FC = () => {
  const matches = useMatches();
  const routeTitle = getNearestTitle(matches);

  const { organizationShortName, workspaceShortName } = useParams<
    'organizationShortName' | 'workspaceShortName'
  >();

  /**
   * Pause workspace & organization data fetching in this Title component - will be fetched by HOC wrappers and this will use data from populated cache
   * When parsing new SSO users fetching user data must finish first before loading workspace/organization data for document title
   */
  const { data: currentWorkspace } = useWorkspaceByShortNameQuery(
    { organizationShortName, workspaceShortName },
    { enabled: false },
  );
  const { data: currentOrganization } = useOrganizationByShortnameQuery(
    { organizationShortName },
    { enabled: false },
  );

  const organizationName = currentOrganization?.name;
  const workspaceName = currentWorkspace?.name;
  const isLoading =
    (organizationShortName && !organizationName) || (workspaceShortName && !workspaceName);

  useEffect(() => {
    if (isLoading) return;
    const title: string[] = ['Control Room'];

    if (organizationName) title.push(organizationName);
    if (workspaceName) title.push(workspaceName);
    if (routeTitle) title.push(routeTitle);

    window.document.title = title.slice(-2).join(' · ');
  }, [isLoading, routeTitle, organizationName, workspaceName]);

  return null;
};
